/**
 * Shared time formatting utilities for both client-public and client-admin.
 * Formats time strings consistently across the application.
 */

/**
 * Formats a time string for display (converts 24-hour to 12-hour with AM/PM).
 * @param timeString - Time string in HH:MM format (24-hour)
 * @returns Formatted time string in 12-hour format (e.g., "10:00 AM")
 */
export function formatTime(timeString: string): string {
  if (!timeString) return "";

  // Parse HH:MM format
  const match = timeString.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return timeString;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];

  if (isNaN(hours) || hours < 0 || hours > 23) return timeString;

  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

  return `${displayHours}:${minutes} ${period}`;
}

/**
 * Formats a time string for HTML time input (HH:MM format).
 * Converts various time formats to HH:MM (24-hour).
 * @param timeString - Time string (could be in various formats like "10:00 AM" or "10:00")
 * @returns Time string in HH:MM format (24-hour)
 */
export function formatTimeForInput(timeString: string): string {
  if (!timeString) return "";

  // If already in HH:MM format, return as-is
  if (/^\d{2}:\d{2}$/.test(timeString)) {
    return timeString;
  }

  // Try to parse time strings like "10:00 AM" or "10:00"
  try {
    // Handle 12-hour format with AM/PM
    const amPmMatch = timeString.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (amPmMatch) {
      let hours = parseInt(amPmMatch[1], 10);
      const minutes = amPmMatch[2];
      const period = amPmMatch[3].toUpperCase();

      if (period === "PM" && hours !== 12) hours += 12;
      if (period === "AM" && hours === 12) hours = 0;

      return `${hours.toString().padStart(2, "0")}:${minutes}`;
    }

    // Handle 24-hour format
    const timeMatch = timeString.match(/(\d{1,2}):(\d{2})/);
    if (timeMatch) {
      const hours = parseInt(timeMatch[1], 10);
      const minutes = timeMatch[2];
      return `${hours.toString().padStart(2, "0")}:${minutes}`;
    }

    return timeString;
  } catch {
    return timeString;
  }
}
